import React, { FC, ReactNode, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import Button from '~/src/components/Button';
import type {
  ClioWorkCopyJob,
  ClioWorkCopyState,
} from '~/src/services/api/clioService';
import { clioDraftFolderLink } from '~/src/utils/clio';
import {
  dismissIntro,
  dismissResults,
  isIntroDismissed,
  isResultsDismissed,
} from './dismissals';

export type ClioWorkCopyTab =
  | 'all'
  | 'needs_attention'
  | 'not_copied'
  | 'in_clio_work';

// The tab a template is listed under. A copy in Clio Work with changes made since it was copied
// is "Not copied": those changes still have to be copied across.
export const tabOf = (
  state: ClioWorkCopyState,
): Exclude<ClioWorkCopyTab, 'all'> => {
  if (state.status === 'failed') {
    return 'needs_attention';
  }
  if (state.status === 'in_clio_work' && !state.out_of_sync) {
    return 'in_clio_work';
  }
  return 'not_copied';
};

interface Props {
  orgFprint: string;
  states: ClioWorkCopyState[];
  latestJob: ClioWorkCopyJob | null;
  clioDraftFolderId: number | null;
  copying: boolean;
  tab: ClioWorkCopyTab;
  onTabChange: (tab: ClioWorkCopyTab) => void;
  // The copyable templates that aren't in Clio Work yet.
  remainingCount: number;
  onCopyAll: () => void;
  // Suggested templates that aren't in Clio Work yet.
  suggestedCount: number;
  onCopySuggested: () => void;
  onSelectRemaining: () => void;
  onSelectOutOfSync: () => void;
  onRetry: (templateIds: number[]) => void;
  // Only the banner for a running copy: the latest copy's results and the intro are about the
  // whole Library, so they're left out of a filtered view of other templates.
  copyingOnly?: boolean;
}

// Looks like a link: the banner's secondary action.
const LinkButton: FC<{ onClick: () => void; children: ReactNode }> = ({
  onClick,
  children,
}) => (
  <button
    type="button"
    className="whitespace-nowrap font-medium text-blue-700 hover:underline"
    onClick={onClick}
  >
    {children}
  </button>
);

// The banner's primary action: a filled gray button.
const BannerButton: FC<{ onClick: () => void; children: ReactNode }> = ({
  onClick,
  children,
}) => (
  <button
    type="button"
    className="whitespace-nowrap rounded bg-gray-100 px-3 py-1.5 font-medium text-gray-900 hover:bg-gray-200"
    onClick={onClick}
  >
    {children}
  </button>
);

const plural = (count: number, one: string, many: string) =>
  count === 1 ? one : many;

const Banner: FC<{
  variant: 'info' | 'warning';
  heading: ReactNode;
  body: ReactNode;
  actions?: ReactNode;
  onClose?: () => void;
}> = ({ variant, heading, body, actions, onClose }) => (
  <div
    role="status"
    className={`mb-4 flex items-center rounded border px-4 py-3 text-sm text-gray-900 ${
      variant === 'warning'
        ? 'border-yellow-200 bg-yellow-50'
        : 'border-blue-200 bg-blue-100'
    }`}
  >
    <div className="flex flex-1 items-start">
      {variant === 'warning' ? (
        <span className="mr-2 text-yellow-700" aria-hidden="true">
          ⚠
        </span>
      ) : (
        <InfoOutlinedIcon
          className="mr-2 mt-px shrink-0"
          sx={{ fontSize: 18 }}
          aria-hidden="true"
        />
      )}
      <div>
        <div className="font-medium">{heading}</div>
        <div className="mt-1">{body}</div>
      </div>
    </div>
    <div className="ml-6 flex items-center gap-4">
      {actions}
      {onClose && (
        <button
          type="button"
          className="flex text-gray-900"
          aria-label="Close"
          onClick={onClose}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </button>
      )}
    </div>
  </div>
);

/**
 * Copying templates to Clio Work from the Library's "Custom templates" table: the banner for
 * where the copy stands, and the tabs that filter the table.
 */
const ClioWorkCopyControls: FC<Props> = ({
  orgFprint,
  states,
  latestJob,
  clioDraftFolderId,
  copying,
  tab,
  onTabChange,
  remainingCount,
  onCopyAll,
  suggestedCount,
  onCopySuggested,
  onSelectRemaining,
  onSelectOutOfSync,
  onRetry,
  copyingOnly = false,
}) => {
  const [introDismissed, setIntroDismissed] = useState(() =>
    isIntroDismissed(orgFprint),
  );
  const [dismissedJobId, setDismissedJobId] = useState<number | null>(null);

  const count = (status: ClioWorkCopyState['status']) =>
    states.filter((s) => s.status === status).length;
  const failed = states.filter((s) => s.status === 'failed');
  const retryableIds = failed
    .filter((s) => s.can_retry)
    .map((s) => s.template_id);
  const copyingCount = count('copying');
  const tabCount = (key: Exclude<ClioWorkCopyTab, 'all'>) =>
    states.filter((s) => tabOf(s) === key).length;

  const resultsShown =
    !!latestJob &&
    !copying &&
    // A cancelled copy still reports the templates it copied before it stopped.
    (latestJob.status === 'completed' ||
      latestJob.status === 'failed' ||
      latestJob.status === 'cancelled') &&
    // Nothing left to report once every copy was deleted in Clio Work, unless the copy itself
    // failed: that can happen before any template was copied.
    (latestJob.copied_count + latestJob.failed_count > 0 ||
      latestJob.status === 'failed') &&
    dismissedJobId !== latestJob.id &&
    !isResultsDismissed(orgFprint, latestJob.id);

  const closeResults = () => {
    if (latestJob) {
      dismissResults(orgFprint, latestJob.id);
      setDismissedJobId(latestJob.id);
    }
  };

  // Retries every failed template with a retry left, earlier copies' failures too, so it names
  // them by what they are rather than by the latest copy's count.
  const retryButton =
    retryableIds.length > 0 ? (
      <Button
        primary
        small
        disabled={copying}
        onClick={() => onRetry(retryableIds)}
      >
        {`Retry ${retryableIds.length} failed ${plural(
          retryableIds.length,
          'template',
          'templates',
        )}`}
      </Button>
    ) : null;

  // Once no failed template has a retry left, the banner sends the user to support instead.
  const contactSupport =
    retryableIds.length > 0 ? null : (
      <a className="underline" href="mailto:support@clio.com">
        Contact our support team.
      </a>
    );

  // Only this copy's templates: one changed since an earlier copy isn't news about this one.
  const outOfSyncCount = latestJob?.changed_template_ids?.length ?? 0;
  const openClioWork = () =>
    window.open(
      clioDraftFolderLink(clioDraftFolderId),
      '_blank',
      'noopener,noreferrer',
    );

  let banner: ReactNode = null;
  if (copying) {
    banner = (
      <Banner
        variant="info"
        heading={`Copying ${copyingCount || 'your'} ${plural(
          copyingCount,
          'template',
          'templates',
        )} to Clio Work`}
        body={
          latestJob?.notify_by_email
            ? "You can leave this page. We'll email you when it's done."
            : 'You can leave this page and check back later.'
        }
      />
    );
  } else if (resultsShown && latestJob && !copyingOnly) {
    const { copied_count: copied, failed_count: failedCount } = latestJob;
    const total = copied + failedCount;
    // The copy itself failed, e.g. it stopped making progress: say it stopped, whatever it got
    // through first.
    if (latestJob.status === 'failed') {
      banner = (
        <Banner
          variant="warning"
          heading="Copying to Clio Work stopped"
          body={
            latestJob.stopped_reason ? (
              // Clio Manage refused the whole firm: say why, which the firm can usually fix.
              <>
                {copied > 0
                  ? `Copying stopped after ${copied} ${plural(
                      copied,
                      'template was',
                      'templates were',
                    )} copied.`
                  : 'No templates were copied.'}{' '}
                {latestJob.stopped_reason}
              </>
            ) : (
              <>
                {copied > 0
                  ? `Something went wrong on our side, and copying stopped after ${copied} ${plural(
                      copied,
                      'template was',
                      'templates were',
                    )} copied.`
                  : 'Something went wrong on our side, and no templates were copied.'}{' '}
                {failed.length > 0 && contactSupport
                  ? contactSupport
                  : 'Try again and if issues persist, contact our support team.'}
              </>
            )
          }
          actions={
            <>
              {count('not_copied') > 0 && (
                <LinkButton onClick={onSelectRemaining}>
                  Select remaining templates
                </LinkButton>
              )}
              {retryButton}
            </>
          }
          onClose={closeResults}
        />
      );
    } else if (failedCount === 0 && copied > 0) {
      banner = (
        <Banner
          variant="info"
          heading={`${copied} ${plural(
            copied,
            'template',
            'templates',
          )} copied to Clio Work`}
          body={
            outOfSyncCount > 0
              ? 'Review these templates in Clio Work. Some changes were made to the Draft templates while they were being copied. Copy the templates again to update the corresponding templates in Clio Work.'
              : plural(
                  copied,
                  'Review this template in Clio Work.',
                  'Review these templates in Clio Work. Going forward, make edits to these templates in Clio Work.',
                )
          }
          actions={
            <>
              {count('not_copied') > 0 && (
                <LinkButton onClick={onSelectRemaining}>
                  Select remaining templates
                </LinkButton>
              )}
              {outOfSyncCount > 0 ? (
                <Button primary small onClick={onSelectOutOfSync}>
                  Select templates with changes made
                </Button>
              ) : (
                <Button primary small onClick={openClioWork}>
                  Open in Clio Work ↗
                </Button>
              )}
            </>
          }
          onClose={closeResults}
        />
      );
    } else if (copied === 0) {
      banner = (
        <Banner
          variant="warning"
          heading={`${total || failed.length} ${plural(
            total || failed.length,
            "template couldn't",
            "templates couldn't",
          )} be copied`}
          body={
            contactSupport ? (
              <>Something went wrong on our side. {contactSupport}</>
            ) : (
              'Something went wrong on our side. Retry again and if issues persist, contact our support team.'
            )
          }
          actions={retryButton}
          onClose={closeResults}
        />
      );
    } else {
      banner = (
        <Banner
          variant="warning"
          heading={`${copied} of ${total} templates copied to Clio Work`}
          body={
            <>
              {`${failedCount} ${plural(
                failedCount,
                "template couldn't",
                "templates couldn't",
              )} be copied. You'll find ${plural(
                failedCount,
                'it',
                'them',
              )} in Needs attention.`}
              {contactSupport && <> {contactSupport}</>}
            </>
          }
          actions={retryButton}
          onClose={closeResults}
        />
      );
    }
  } else if (!copyingOnly && !introDismissed && remainingCount > 0) {
    banner = (
      <Banner
        variant="info"
        heading="You can now migrate your templates to Clio Work"
        body="Bring your custom Word templates into Clio Work, where your firm will draft from now on. Your templates in Clio Draft will remain as is."
        actions={
          <>
            <LinkButton onClick={onCopyAll}>
              Migrate all {remainingCount}
            </LinkButton>
            {suggestedCount > 0 && (
              <BannerButton onClick={onCopySuggested}>
                Migrate suggested
              </BannerButton>
            )}
          </>
        }
        onClose={() => {
          dismissIntro(orgFprint);
          setIntroDismissed(true);
        }}
      />
    );
  }

  const tabs: { key: ClioWorkCopyTab; label: string }[] = [
    { key: 'all', label: `All (${states.length})` },
    { key: 'needs_attention', label: `Needs attention (${failed.length})` },
    {
      key: 'not_copied',
      label: `Not in Clio Work (${tabCount('not_copied')})`,
    },
    {
      key: 'in_clio_work',
      label: `In Clio Work (${tabCount('in_clio_work')})`,
    },
  ];

  return (
    <div>
      {banner}
      <div role="tablist" className="mb-3 flex border-b text-sm">
        {tabs.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            className={`mr-4 pb-1 ${
              tab === key
                ? 'border-b-2 border-blue-600 font-semibold text-blue-700'
                : 'text-gray-600'
            }`}
            onClick={() => onTabChange(key)}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default ClioWorkCopyControls;
